import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  isBulkReportCode,
  type BulkJobListItem,
  type BulkReportCode,
  type BulkReportItem,
} from '@designdeck/shared';
import {
  Button,
  StatusPill,
  cardVariants,
  type StatusPillProps,
} from '@/components/ui';
import { useBulkJob, useDismissBulkJob } from '@/hooks/useBulkCreation';
import { formatDate } from '@/lib/format-date';
import { cn } from '@/lib/utils';

/** Codes that only confirm success; the row list never repeats them. */
const SILENT_CODES: ReadonlySet<BulkReportCode> = new Set<BulkReportCode>([
  'row_created',
  'published',
]);

/** Notes whose listing waits in the Review Gate for a retry. */
const REVIEW_CODES: ReadonlySet<BulkReportCode> = new Set<BulkReportCode>([
  'publish_failed',
  'mockups_incomplete',
]);

type CardState =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'stalled';

const STATE_PRESENTATION: Record<
  CardState,
  { variant: NonNullable<StatusPillProps['variant']>; labelKey: string }
> = {
  queued: {
    variant: 'neutral',
    labelKey: 'seller.bulk.progress.status.queued',
  },
  running: {
    variant: 'neutral',
    labelKey: 'seller.bulk.progress.status.running',
  },
  completed: {
    variant: 'published',
    labelKey: 'seller.bulk.progress.status.completed',
  },
  failed: { variant: 'danger', labelKey: 'seller.bulk.progress.status.failed' },
  cancelled: {
    variant: 'archived',
    labelKey: 'seller.bulk.progress.status.cancelled',
  },
  // A job the sweep gave up on reads as cancelled, not as a hard failure:
  // the rows reported until then exist.
  stalled: {
    variant: 'warning',
    labelKey: 'seller.bulk.progress.status.cancelled',
  },
};

function cardState(job: BulkJobListItem): CardState {
  if (job.jobError?.code === 'stalled') return 'stalled';
  switch (job.status) {
    case 'QUEUED':
      return 'queued';
    case 'RUNNING':
      return 'running';
    case 'COMPLETED':
      return 'completed';
    case 'CANCELLED':
      return 'cancelled';
    case 'FAILED':
      return 'failed';
  }
}

export interface BulkJobCounts {
  created: number;
  withNotes: number;
  failed: number;
  /** Rows of the run, reported or not. */
  total: number;
  /** Rows the job has reported so far. */
  reported: number;
}

/**
 * The four counters of the card, derived from the report outcomes. While
 * the job runs only the first report page is loaded, so the reported count
 * also takes the list summary into account and never runs behind it.
 */
export function countBulkJob(
  job: BulkJobListItem,
  items: readonly BulkReportItem[]
): BulkJobCounts {
  const counts = { created: 0, withNotes: 0, failed: 0 };
  for (const item of items) {
    if (item.outcome === 'created') counts.created += 1;
    else if (item.outcome === 'created_with_notes') counts.withNotes += 1;
    else counts.failed += 1;
  }
  const summarized = job.summary
    ? job.summary.succeeded + job.summary.failed
    : 0;
  const total = Math.max(job.itemCount, job.summary?.total ?? 0);
  return {
    ...counts,
    total,
    reported: Math.min(total, Math.max(items.length, summarized)),
  };
}

/**
 * Codes a more specific one replaces: a publish that failed BECAUSE the
 * mockups were incomplete reports both, and the two messages read almost
 * identically — only the specific reason is shown.
 */
const SUPERSEDED_BY: ReadonlyMap<BulkReportCode, BulkReportCode> = new Map([
  ['publish_failed', 'mockups_incomplete'],
]);

function visibleCodes(item: BulkReportItem): BulkReportCode[] {
  const codes = item.codes.filter(
    (code) => isBulkReportCode(code) && !SILENT_CODES.has(code)
  );
  const errorCode = item.error?.code;
  if (
    errorCode &&
    isBulkReportCode(errorCode) &&
    !SILENT_CODES.has(errorCode) &&
    !codes.includes(errorCode)
  ) {
    codes.push(errorCode);
  }
  return codes.filter((code) => {
    const supersedes = SUPERSEDED_BY.get(code);
    return supersedes === undefined || !codes.includes(supersedes);
  });
}

function fileNameOf(item: BulkReportItem): string | null {
  const value = item.params?.fileName;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export interface BulkJobCardProps {
  job: BulkJobListItem;
}

/**
 * Progress of one bulk run in the design overview (plan U11): reference
 * name and start, the counters created / with notes / failed / total, the
 * rows that need attention with their translated codes, a way to the
 * Review Gate when listings wait there, and "Ausblenden" once the job is
 * terminal.
 */
export function BulkJobCard({ job }: BulkJobCardProps) {
  const { t, i18n } = useTranslation();
  const detail = useBulkJob(job.id);
  const dismiss = useDismissBulkJob();

  const state = cardState(job);
  const presentation = STATE_PRESENTATION[state];
  const active = state === 'queued' || state === 'running';
  const items = [...(detail.data?.items ?? [])].sort(
    (a, b) => a.index - b.index
  );
  const counts = countBulkJob(job, items);
  const attentionRows = items.filter((item) => item.outcome !== 'created');
  const createdCount = counts.created + counts.withNotes;
  const waitsInReview =
    (job.asDraft && createdCount > 0) ||
    items.some((item) => item.codes.some((code) => REVIEW_CODES.has(code)));

  const title = t('seller.bulk.progress.title', {
    reference: job.referenceName,
  });
  const startedAt = formatDate(job.startedAt ?? job.createdAt, i18n.language);
  const percent =
    counts.total > 0 ? Math.round((counts.reported / counts.total) * 100) : 0;

  const counters: { key: keyof BulkJobCounts; labelKey: string }[] = [
    { key: 'created', labelKey: 'seller.bulk.progress.counts.created' },
    { key: 'withNotes', labelKey: 'seller.bulk.progress.counts.withNotes' },
    { key: 'failed', labelKey: 'seller.bulk.progress.counts.failed' },
    { key: 'total', labelKey: 'seller.bulk.progress.counts.total' },
  ];

  return (
    <section
      aria-label={title}
      className={cn(cardVariants({ padding: 'sm' }), 'flex flex-col gap-4')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {active && (
              <Loader2
                className="h-4 w-4 shrink-0 animate-spin text-primary"
                aria-hidden="true"
              />
            )}
            <h3 className="dc-h3 truncate text-base text-ink">{title}</h3>
            <StatusPill variant={presentation.variant}>
              {t(presentation.labelKey)}
            </StatusPill>
            {job.asDraft && (
              <StatusPill variant="draft">
                {t('seller.bulk.progress.draftMode')}
              </StatusPill>
            )}
          </div>
          {startedAt && (
            <p className="text-[12.5px] text-mute">
              {t('seller.bulk.progress.startedAt', { date: startedAt })}
            </p>
          )}
        </div>
        {!active && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('seller.bulk.progress.closeLabel', {
              reference: job.referenceName,
            })}
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate(job.id)}
          >
            {t('seller.bulk.progress.close')}
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {counters.map(({ key, labelKey }) => (
          <div
            key={key}
            className="flex flex-col gap-0.5 rounded-control bg-paper px-3 py-2 ring-1 ring-line-2"
          >
            <dt className="text-[12.5px] text-mute">{t(labelKey)}</dt>
            <dd
              className={cn(
                'font-mono text-lg font-medium text-ink',
                key === 'failed' && counts.failed > 0 && 'text-danger'
              )}
            >
              {counts[key]}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-col gap-2">
        {active && (
          <div
            className="h-1.5 overflow-hidden rounded-full bg-paper-3"
            aria-hidden="true"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
        )}
        <p aria-live="polite" className="text-[12.5px] text-mute">
          {state === 'stalled'
            ? t('seller.bulk.progress.stalled')
            : t('seller.bulk.progress.reported', {
                reported: counts.reported,
                total: counts.total,
              })}
        </p>
      </div>

      {attentionRows.length > 0 && (
        <ul className="flex flex-col divide-y divide-line-2 rounded-control bg-paper ring-1 ring-line-2">
          {attentionRows.map((item) => {
            const fileName = fileNameOf(item);
            const failed = item.outcome === 'failed';
            return (
              <li key={item.index} className="flex flex-col gap-0.5 px-3 py-2">
                <span className="text-[13px] font-medium text-ink">
                  {fileName
                    ? t('seller.bulk.progress.row', {
                        number: item.index + 1,
                        fileName,
                      })
                    : t('seller.bulk.progress.rowWithoutFile', {
                        number: item.index + 1,
                      })}
                </span>
                {visibleCodes(item).map((code) => (
                  <span
                    key={code}
                    className={cn(
                      'text-[12.5px]',
                      failed ? 'text-danger' : 'text-ink-2'
                    )}
                  >
                    {t(`seller.bulk.codes.${code}`, item.params)}
                  </span>
                ))}
              </li>
            );
          })}
        </ul>
      )}

      {waitsInReview && (
        <div>
          <Button asChild variant="outline" size="sm">
            <Link to="/review">{t('seller.bulk.progress.openReview')}</Link>
          </Button>
        </div>
      )}
    </section>
  );
}
